import React from 'react'
import PropTypes from 'prop-types'
import { FlowGrid, H6, TextField } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { getPersonName, getNestedOrFloatingElementById, useSlsStateContext } from '@fs/zion-sls'
import { FormAutosuggest } from '@fs/zion-form'
import useR2RelationshipOptions from '../../../../views/indexing/hooks/useR2RelationshipOptions'

/**
 * Renders form fields for a relationship event, including the current person's name
 * and an autosuggest field for selecting another person (e.g., spouse).
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.person - The person object.
 * @param {number} props.index - The index of the relationship event in the field list.
 * @param {string} props.fieldNamePrefix - The prefix used for form field names.
 * @returns {JSX.Element} The rendered relationship event fields.
 */
const RelationshipEventFields = ({ person, index, fieldNamePrefix }) => {
  const [t] = useTranslation()
  const personName = getPersonName({ person })
  const { images } = useSlsStateContext()
  const otherPersonFieldName = `${fieldNamePrefix}[${index}].relationship.otherPerson`

  const personSuggestions = useR2RelationshipOptions({
    person,
    currentPersonRecord: getNestedOrFloatingElementById(images, person?.recordId),
  })?.filter((option) => option.key !== person?.id)

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="nano">
      <H6>{t('relationship-event-fields.title', 'Relationship')}</H6>
      <TextField label={t('relationship-event-fields.spouse', 'Spouse')} value={personName} disabled dense />
      <FormAutosuggest
        label={t('relationship-event-fields.spouse', 'Spouse')}
        name={otherPersonFieldName}
        suggestions={personSuggestions}
        data-testid={`event-relationship-other-person-${index}`}
        dense
        clearable
      />
    </FlowGrid>
  )
}

export default RelationshipEventFields

RelationshipEventFields.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.string,
    recordId: PropTypes.string,
  }),
  index: PropTypes.number.isRequired,
  fieldNamePrefix: PropTypes.string.isRequired,
}
